import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Mail, MapPin } from "lucide-react";
import { BRAND } from "../lib/brand";

const COMPANY_LINKS = [
  { to: "/services", label: "Services" },
  { to: "/data-and-audience", label: "Data & Audience" },
  { to: "/partners", label: "Partners" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

const LEGAL_LINKS = [
  { to: "/legal", label: "Legal Centre" },
  { to: "/legal/privacy-policy", label: "Privacy Policy" },
  { to: "/legal/cookie-policy", label: "Cookie Policy" },
  { to: "/legal/terms", label: "Website Terms" },
  { to: "/legal/sms-email-marketing-policy", label: "SMS & Email Marketing" },
  { to: "/legal/acceptable-traffic-sources-policy", label: "Acceptable Traffic Sources" },
  { to: "/legal/data-usage-agreement", label: "Data Usage Agreement" },
];

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="bg-[#0A0E1A] text-slate-300 relative overflow-hidden">
      <div className="absolute inset-0 grid-bg opacity-50 pointer-events-none" />
      <div aria-hidden className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-400/40 to-transparent" />
      <div className="relative container-pr pt-24 pb-10">
        {/* Editorial outro headline */}
        <div className="mb-16 max-w-3xl">
          <p className="text-[11px] uppercase tracking-[0.24em] text-cyan-300/90 font-semibold">§ Closing</p>
          <h3 className="font-display mt-4 text-3xl md:text-5xl font-medium tracking-[-0.03em] leading-[1.04] text-white">
            A serious partner for{" "}
            <span className="font-serif-edit italic font-normal text-cyan-300">player acquisition.</span>
          </h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="flex items-center gap-2.5">
              <span className="relative inline-flex items-center justify-center h-9 w-9 rounded-md bg-white/5 border border-white/10">
                <span className="h-2 w-2 rounded-full bg-cyan-400" />
              </span>
              <span className="font-display text-xl font-semibold text-white">
                Player<span className="text-cyan-400">Reach</span>
              </span>
            </div>
            <p className="mt-5 text-sm leading-relaxed text-slate-400 max-w-md">
              Performance marketing, customer acquisition and audience intelligence for iGaming,
              sports betting and entertainment brands. Built around documented data, partner due
              diligence and accountable campaign delivery.
            </p>

            <div className="mt-7 flex flex-col gap-3 text-sm">
              <a href={`mailto:${BRAND.contactEmail}`} className="inline-flex items-center gap-2 text-slate-300 hover:text-white transition-colors" data-testid="footer-email">
                <Mail className="h-4 w-4 text-cyan-400" />
                {BRAND.contactEmail}
              </a>
              <div className="inline-flex items-start gap-2 text-slate-400">
                <MapPin className="h-4 w-4 text-cyan-400 mt-0.5 flex-shrink-0" />
                <span>{BRAND.address}</span>
              </div>
            </div>

            <Link to="/contact" className="mt-8 inline-flex items-center gap-2 text-white font-medium border-b border-cyan-400/40 pb-1 hover:border-cyan-400 transition-colors" data-testid="footer-cta-conversation">
              Start a Conversation <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="md:col-span-3">
            <p className="text-xs uppercase tracking-[0.18em] font-semibold text-white/60">Company</p>
            <ul className="mt-5 space-y-3">
              {COMPANY_LINKS.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="text-sm text-slate-300 hover:text-white transition-colors" data-testid={`footer-link-${l.label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4">
            <p className="text-xs uppercase tracking-[0.18em] font-semibold text-white/60">Legal Centre</p>
            <ul className="mt-5 space-y-3">
              {LEGAL_LINKS.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="text-sm text-slate-300 hover:text-white transition-colors" data-testid={`footer-legal-${l.label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-8 border-t border-white/10 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="text-xs text-slate-500">
            <p>
              PlayerReach.io is a trading brand of <span className="text-slate-300">{BRAND.legalEntity}</span>,
              Formation Number {BRAND.formationNumber}.
            </p>
            <p className="mt-1">{BRAND.copyright} All rights reserved.</p>
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-400">
            <span className="pulse-dot" />
            Live operations · Compliance focused
          </div>
        </div>
      </div>
    </footer>
  );
}
